import React, { useState, useEffect, useRef } from "react";
import { MapPin } from "lucide-react";
import { fetchCitySuggestions } from "@/services/eventApi";

const CityAutocomplete = ({ 
  value, 
  onChange, 
  label, 
  placeholder = "City...", 
  error, 
  className = "", 
  inputClassName = "",
  required = false, 
  name,
  onEnter // Optional callback for when enter is pressed
}) => {
  const [localCity, setLocalCity] = useState(value || "");
  const [suggestions, setSuggestions] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const containerRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  const suggestionRequestRef = useRef(0);

  // Sync external value
  useEffect(() => {
    setLocalCity(value || "");
  }, [value]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleChange = (e) => {
    const newVal = e.target.value;
    setLocalCity(newVal);
    // Let parent know the value immediately so form state updates
    if (onChange) {
      if (typeof onChange === 'function' && newVal !== undefined) {
          // If used directly with react-hook-form's onChange or similar
          onChange(newVal);
      }
    }
    
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }

    if (newVal.trim().length < 2) {
      setSuggestions([]);
      setShowDropdown(false);
      return;
    }

    typingTimeoutRef.current = setTimeout(async () => {
      const currentReq = ++suggestionRequestRef.current;
      try {
        const res = await fetchCitySuggestions(newVal.trim());
        if (currentReq === suggestionRequestRef.current) {
          setSuggestions(res);
          setShowDropdown(true);
        }
      } catch {
        if (currentReq === suggestionRequestRef.current) {
          setSuggestions([]);
        }
      }
    }, 300);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault(); // Prevent form submission
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
      setSuggestions([]);
      setShowDropdown(false);
      if (onEnter) onEnter(localCity);
    }
  };

  const handleSelect = (city) => {
    setLocalCity(city);
    setSuggestions([]);
    setShowDropdown(false);
    if (onChange) onChange(city);
    if (onEnter) onEnter(city);
  };

  return (
    <div className={`w-full ${className}`} ref={containerRef}>
      {label && (
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5 flex justify-between">
          <span>{label} {required && <span className="text-red-500">*</span>}</span>
        </label>
      )}
      <div className="relative">
        <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <input
          type="text"
          placeholder={placeholder}
          value={localCity}
          name={name}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onFocus={() => {
            if (suggestions.length > 0) setShowDropdown(true);
          }}
          className={`w-full pl-9 pr-3 py-2 text-sm border ${
            error ? "border-red-500" : "border-gray-200 dark:border-gray-700"
          } rounded-md bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all outline-none ${inputClassName}`}
        />
        {/* City Suggestions Dropdown */}
        {showDropdown && suggestions.length > 0 && (
          <div className="absolute z-50 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md shadow-lg max-h-60 overflow-y-auto">
            <ul className="py-1">
              {suggestions.map((city, index) => (
                <li
                  key={index}
                  onClick={() => handleSelect(city)}
                  className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-amber-50 dark:hover:bg-amber-900/40 cursor-pointer flex items-center gap-2"
                >
                  <MapPin className="w-3.5 h-3.5 text-gray-400 dark:text-gray-500" />
                  {city}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
      {error && (
        <p className="mt-1.5 text-sm text-red-500">{error}</p>
      )}
    </div>
  );
};

export default CityAutocomplete;
